import { Layers } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import type { Basemap, MapViewState } from "@/lib/useMapView";
import { useLanguage } from "@/i18n";

/**
 * A compact Layers menu for a map shown without the Filters & Layers panel
 * (the block detail map on the Zones, wards & blocks page): base map and the
 * same layer switches, in one dropdown over the map's corner.
 */
export default function MapLayersMenu({ state }: { state: MapViewState }) {
  const { t } = useLanguage();
  const w = t.workspace;
  const { view } = state;
  const toggle = (key: string) => (e: Event) => {
    e.preventDefault(); // keep the menu open while switching several
    state.toggleLayer(key);
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1.5 rounded-md bg-surface-raised/95 px-2.5 py-1.5 text-2xs font-bold uppercase tracking-wide text-ink shadow-menu backdrop-blur hover:bg-surface-sunken"
        >
          <Layers className="h-3.5 w-3.5" /> {w.layerTab}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel>{w.baseMap}</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={view.basemap} onValueChange={(v) => state.setBasemap(v as Basemap)}>
          <DropdownMenuRadioItem value="street">{w.street}</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="satellite">{w.satellite}</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="none">{w.none}</DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{w.boundaries}</DropdownMenuLabel>
        <DropdownMenuCheckboxItem checked={view.visible.zone} onSelect={toggle("zone")}>
          {t.mapPreview.boundaryZones}
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem checked={view.visible.ward} onSelect={toggle("ward")}>
          {t.mapPreview.boundaryWards}
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem checked={view.visible.block} onSelect={toggle("block")}>
          {t.mapPreview.boundaryBlocks}
        </DropdownMenuCheckboxItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{w.surveyData}</DropdownMenuLabel>
        <DropdownMenuCheckboxItem checked={view.visible.features} onSelect={toggle("features")}>
          {t.mapPreview.fieldData}
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem checked={view.visible.labels} onSelect={toggle("labels")}>
          {t.mapPreview.propertyNumbers}
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem checked={view.visible.imagery} onSelect={toggle("imagery")}>
          {t.mapPreview.imagery}
        </DropdownMenuCheckboxItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
